import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { deleteArticle, listArticles } from "../articlesApi";
import { ArticleListItem } from "../types";
import { SkeletonRow, SummaryCard } from "../components/Skeleton";

type StatusFilter = "all" | "published" | "draft";

// Rows shown by the loading skeleton, and one width per table column
// (标题, 类型, 状态, Slug, 发布时间, [actions]) -- picked to roughly match a
// typical result set so the table doesn't visibly resize once real rows
// swap in.
const SKELETON_ROWS = 8;
const SKELETON_WIDTHS = ["w-48", "w-16", "w-14", "w-24", "w-20", "w-10"];

export default function AdminArticleListPage() {
  const [articles, setArticles] = useState<ArticleListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const navigate = useNavigate();

  async function refresh() {
    try {
      setArticles(await listArticles());
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败");
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const loading = articles === null;

  const summary = useMemo(() => {
    const list = articles ?? [];
    return {
      total: list.length,
      published: list.filter((a) => a.status === "published").length,
      draft: list.filter((a) => a.status === "draft").length,
    };
  }, [articles]);

  const filtered = useMemo(() => {
    const list = articles ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (q && !`${a.title} ${a.slug}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [articles, search, statusFilter]);

  async function handleDelete(article: ArticleListItem) {
    if (!window.confirm(`确定要删除「${article.title}」吗？此操作不可撤销。`)) return;
    await deleteArticle(article.id);
    refresh();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-secondary-dark">文章管理</h1>
        <button
          onClick={() => navigate("/admin/articles/new")}
          className="bg-gold hover:bg-gold-light text-secondary-dark font-medium rounded-full px-5 py-2 text-sm transition-colors"
        >
          + 新建文章
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6 max-w-lg">
        <SummaryCard label="全部文章" value={loading ? null : summary.total} />
        <SummaryCard label="已发布" value={loading ? null : summary.published} />
        <SummaryCard label="草稿" value={loading ? null : summary.draft} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜索标题 / slug"
          className="border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white w-64"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white"
        >
          <option value="all">全部状态</option>
          <option value="published">已发布</option>
          <option value="draft">草稿</option>
        </select>
        {articles && (
          <span className="text-xs text-secondary-light">
            显示 {filtered.length} / {articles.length} 篇
          </span>
        )}
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <div className="bg-white rounded-xl border border-fourth-light overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-c-gray text-left text-secondary-light uppercase text-xs tracking-wide">
              <th className="px-4 py-3">标题</th>
              <th className="px-4 py-3">类型</th>
              <th className="px-4 py-3">状态</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">发布时间</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <SkeletonRow key={i} widths={SKELETON_WIDTHS} />
              ))}
            {articles !== null && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-secondary-light">
                  {articles.length === 0 ? "还没有任何文章，点击右上角「新建文章」开始。" : "没有匹配当前筛选条件的文章。"}
                </td>
              </tr>
            )}
            {filtered.map((article) => (
              <tr key={article.id} className="border-t border-fourth-light hover:bg-c-gray/60">
                <td className="px-4 py-3">
                  <div className="font-medium text-secondary-dark">{article.title}</div>
                  {article.excerpt && (
                    <div className="text-secondary-light text-xs truncate max-w-md">{article.excerpt}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      article.type === "external_link"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-purple-100 text-purple-700"
                    }`}
                  >
                    {article.type === "external_link" ? "外部链接" : "文章"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      article.status === "published"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {article.status === "published" ? "已发布" : "草稿"}
                  </span>
                </td>
                <td className="px-4 py-3 text-secondary-light">{article.slug}</td>
                <td className="px-4 py-3 text-secondary-light">
                  {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString("zh-CN") : "-"}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link
                    to={`/admin/articles/${article.id}`}
                    className="text-gold hover:text-secondary-dark transition-colors mr-4"
                  >
                    编辑
                  </Link>
                  <button
                    onClick={() => handleDelete(article)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    删除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
